import { Star, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useState, useEffect, useRef } from 'react';
import { apiSync } from '@/lib/api';

interface Review {
  name: string;
  rating: number;
  text: string;
  date: string;
}

interface ReviewData {
  reviews: Review[];
  totalReviews: number;
  averageRating: number;
  lastUpdated: string;
}

const fallbackReviewData: ReviewData = {
  reviews: [
    {
      name: 'Anouk V.',
      rating: 5,
      text: 'Wat een geweldige ervaring! De alpaca\'s zijn zo lief en de picknick was heerlijk. Echt een aanrader voor een leuk uitje met het gezin.',
      date: '15-2-2026'
    },
    {
      name: 'Martijn K.',
      rating: 5,
      text: 'Super leuk uitje voor jong en oud. De alpaca\'s zijn erg vriendelijk en de omgeving is prachtig. We komen zeker terug!',
      date: '8-2-2026'
    },
    {
      name: 'Lisanne B.',
      rating: 5,
      text: 'De high tea was fantastisch en de alpaca\'s zijn zo schattig. Perfecte combinatie van lekker eten en dieren. Aanrader!',
      date: '1-2-2026'
    },
    {
      name: 'Ruben D.',
      rating: 5,
      text: 'Topervaring! De kinderen waren door het dolle heen. Mooie locatie midden in Dordrecht, dat had ik niet verwacht.',
      date: '25-1-2026'
    }
  ],
  totalReviews: 47,
  averageRating: 4.7,
  lastUpdated: '2026-03-01'
};

const GoogleReviews = () => {
  const syncReviews = apiSync.getReviews();
  const initialData = syncReviews.reviews.length > 0 ? syncReviews : fallbackReviewData;
  const [reviewData] = useState<ReviewData>(initialData);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const textRefs = useRef<(HTMLQuoteElement | null)[]>([]);
  const [canExpand, setCanExpand] = useState<boolean[]>([]);
  const [collapsedHeights, setCollapsedHeights] = useState<number[]>([]);
  const [expandedHeights, setExpandedHeights] = useState<number[]>([]);
  const [collapsingIndex, setCollapsingIndex] = useState<number | null>(null);

  useEffect(() => {
    const results = reviewData.reviews.map(r => {
      const wordCount = r.text.trim().split(/\s+/).length;
      return wordCount > 32;
    });
    setCanExpand(results);
  }, [reviewData]);

  useEffect(() => {
    let rafId: number;
    const measure = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const newCollapsed: number[] = [];
        const newExpanded: number[] = [];
        
        textRefs.current.forEach(el => {
          if (!el) {
            newCollapsed.push(0);
            newExpanded.push(0);
            return;
          }
          
          const style = window.getComputedStyle(el);
          const lineHeight = parseFloat(style.lineHeight || '24');
          const collapsed = Math.ceil(lineHeight * 4 + 2);
          const expanded = el.scrollHeight;
          
          newCollapsed.push(collapsed);
          newExpanded.push(expanded);
        });
        
        setCollapsedHeights(newCollapsed);
        setExpandedHeights(newExpanded);
      });
    };
    
    measure();
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', measure);
    };
  }, [reviewData]);

  const toggleExpanded = (index: number) => {
    setExpandedIndex(prev => {
      if (prev === index) {
        setCollapsingIndex(index);
        return null;
      }
      if (prev !== null) {
        setCollapsingIndex(prev);
      }
      return index;
    });
  };

  const parseDutchDate = (dateStr: string): Date => {
    if (!dateStr) return new Date(0);
    const dmy = dateStr.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})$/);
    if (dmy) {
      const day = parseInt(dmy[1], 10);
      const month = parseInt(dmy[2], 10) - 1;
      const year = parseInt(dmy[3].length === 2 ? `20${dmy[3]}` : dmy[3], 10);
      return new Date(year, month, day);
    }
    const fallback = new Date(dateStr);
    return isNaN(fallback.getTime()) ? new Date(0) : fallback;
  };

  if (reviewData.reviews.length === 0) return null;

  return (
    <section className="py-20 bg-muted/30" aria-labelledby="reviews-heading">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16 scroll-fade-in">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg" 
              alt="Google logo" 
              className="h-7 mr-3" 
              loading="lazy"
            />
            <span className="text-2xl font-bold text-foreground">Reviews</span>
          </div>
          
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center space-x-1 mr-3" role="img" aria-label={`${reviewData.averageRating} sterren waardering`}>
              {[1, 2, 3, 4, 5].map(star => (
                <Star 
                  key={star} 
                  className={`w-6 h-6 ${star <= Math.round(reviewData.averageRating) ? 'fill-[#FBBC04] text-[#FBBC04]' : 'text-border'}`} 
                  aria-hidden="true" 
                />
              ))}
            </div>
            <span className="text-xl font-semibold text-foreground">{reviewData.averageRating} van 5</span>
          </div>
          
          <p id="reviews-heading" className="text-muted-foreground">
            Gebaseerd op {reviewData.totalReviews} Google reviews
          </p>
        </div>

        <div className="grid items-start md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" role="list">
          {[...reviewData.reviews]
            .sort((a, b) => parseDutchDate(b.date).getTime() - parseDutchDate(a.date).getTime())
            .slice(0, 4)
            .map((review, index) => {
              const isExpanded = expandedIndex === index;
              return (
                <Card key={index} className="p-6 hover:shadow-lg transition-shadow flex flex-col" role="listitem">
                  <article className="flex flex-col h-full">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mr-3" aria-hidden="true">
                        <span className="text-primary-foreground font-semibold text-sm">
                          {review.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-foreground">{review.name}</div>
                        <div className="flex items-center" role="img" aria-label={`${review.rating} van 5 sterren`}>
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star 
                              key={star} 
                              className={`w-4 h-4 ${star <= review.rating ? 'fill-[#FBBC04] text-[#FBBC04]' : 'text-border'}`} 
                              aria-hidden="true" 
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-grow mb-4">
                      <div 
                        className="transition-[max-height] duration-700 ease-in-out overflow-hidden" 
                        style={{
                          maxHeight: isExpanded 
                            ? `${expandedHeights[index] || 1000}px` 
                            : `${collapsedHeights[index] || 104}px`
                        }}
                        onTransitionEnd={(e) => {
                          if (e.currentTarget !== e.target) return;
                          if (collapsingIndex === index) setCollapsingIndex(null);
                        }}
                      >
                        <blockquote 
                          ref={(el) => { textRefs.current[index] = el; }} 
                          className="text-muted-foreground leading-relaxed" 
                          style={!isExpanded && collapsingIndex !== index ? {
                            display: '-webkit-box',
                            WebkitLineClamp: 4,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          } as React.CSSProperties : undefined}
                        >
                          "{review.text}"
                        </blockquote>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-auto">
                      <time className="text-sm text-muted-foreground" dateTime={review.date}>
                        {review.date}
                      </time>
                      
                      {canExpand[index] && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => toggleExpanded(index)} 
                          className="p-0 h-auto text-primary hover:text-primary/80 text-sm" 
                          aria-expanded={isExpanded}
                        >
                          {isExpanded ? (
                            <>Toon minder <ChevronUp className="w-4 h-4 ml-1" /></>
                          ) : (
                            <>Lees meer <ChevronDown className="w-4 h-4 ml-1" /></>
                          )}
                        </Button>
                      )}
                    </div>
                  </article>
                </Card>
              );
            })}
        </div>

        <div className="text-center">
          <a 
            href="https://www.google.com/search?newwindow=1&sa=X&sca_esv=f23db4616a8d669b&sxsrf=ANbL-n7UE5NMZ8OpeK017eR19ZeItodGBA:1774630664953&q=Alpacaboerderij+Dordrecht+Reviews&rflfq=1&num=20&stick=H4sIAAAAAAAAAONgkxI2NjQwNjc0tbAwMTM3s7QAIssNjIyvGBUdcwoSkxOT8lOLUlKLMrMUXPKLUopSkzNKFIJSyzJTy4sXsRJWAwDN2KvAYgAAAA&rldimm=3103715884676986989&tbm=lcl&hl=nl-NL&ved=2ahUKEwjDlZGzxsCTAxWvzQIHHXkgEr4Q9fQKegQIURAG&biw=2296&bih=1212&dpr=2#lkt=LocalPoiReviews" 
            className="inline-flex items-center text-primary hover:text-primary/80 font-semibold rounded px-2 py-1 transition-colors" 
            target="_blank" 
            rel="noopener noreferrer"
            aria-label="Bekijk alle reviews op Google (opent in nieuw venster)"
          >
            Bekijk alle reviews op Google
            <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </a>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviews;
